import { ArrowRightIcon, EyeIcon } from '../icons'
import TypographyParagraph from '../typography/TypographyParagraph'
import TypographyLabel from '../typography/TypographyLabel'
import Button from '../buttons/button'
import InternalLink from '../common/internalLink'
import { formatMetadataType, mapIcon } from '#app/utils/misc.tsx'

const Countersign = ({
	metadata = {
		originalReference: { properties: {} },
		properties: {},
		template: {},
	},
	displayIcon = true,
}: {
	metadata: any
	displayIcon?: boolean
}) => {
	const truncateStringInMiddle = (str: string) => {
		if (!str) return ''
		if (!isValidEthereumAddress(str) && !isValidSolanaAddress(str)) return str
		if (str.length > 8) {
			return (
				str.substring(0, 4) + '...' + str.substring(str.length - 4, str.length)
			)
		} else {
			return str
		}
	}

	const isValidEthereumAddress = (address: string) => {
		const regex = /^(0x)?[0-9a-fA-F]{40}$/
		return regex.test(address)
	}

	const isValidSolanaAddress = (address: string) => {
		const regex = /^[1-9A-HJ-NP-Za-km-z]{44}$/
		return regex.test(address)
	}

	const mapText = (metadata: any) => {
		if (metadata.template.slug == 'countersign' && metadata.originalReference) {
			if (
				metadata.originalReference.countersigningAddress &&
				metadata.address &&
				metadata.originalReference.countersigningAddress.toLowerCase() ===
					metadata.address.toLowerCase()
			) {
				return `countersigned ${metadata.originalReference && metadata.originalReference.template ? metadata.originalReference.template.name : ''}`
			} else {
				return `co-signed ${metadata.originalReference && metadata.originalReference.template ? metadata.originalReference.template.name : ''}`
			}
		} else {
			return `signed ${formatMetadataType(metadata.template.slug)}`
		}
	}

	function getTimeDifferenceString(timestampString: string) {
		const timestamp = new Date(timestampString).getTime()
		const currentTime = new Date().getTime()
		const difference = currentTime - timestamp
		const daysDifference = Math.ceil(difference / (1000 * 60 * 60 * 24))
		return daysDifference + 'd'
	}

	function getMetadataChain(metadata: any) {
		const references = metadata.references.entry
			? metadata.references.entry
			: metadata.references
		const chain = references.chain ? references.chain.name : false
		return chain
	}

	function getMetadataNFT(metadata: any) {
		const references = metadata.references.entry
			? metadata.references.entry
			: metadata.references
		const nft = references.nft ? references.nft : false
		return nft
	}

	function getMetadataProfileURL(metadata: any) {
		const chain = getMetadataChain(metadata)
		if (chain) {
			const nft = getMetadataNFT(metadata)
			if (nft) {
				if (chain === 'solana') {
					let tokenId: string | undefined
					if (nft.tokenId) tokenId = nft.tokenId
					if (nft.tokenAddress) tokenId = nft.tokenAddress
					if (tokenId) {
						return `/metadataProfile/${chain}/${tokenId}/${metadata.properties.originalAttestationIPFSCID}`
					} else {
						return false
					}
				} else {
					return `/metadataProfile/${chain}/${nft.contractAddress}/${nft.tokenId}/${metadata.properties.originalAttestationIPFSCID}`
				}
			} else {
				return false
			}
		} else {
			return false
		}
	}

	const metadataProfileURL = getMetadataProfileURL(metadata)

	return (
		<div className="flex flex-col">
			{metadata.originalReference && metadata.originalReference.template && (
				<div className="mt-4 flex h-full w-full flex-col">
					{metadata.properties.description && (
						<TypographyParagraph
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{metadata.properties.description}
						</TypographyParagraph>
					)}
					{metadata.originalReference &&
						metadata.originalReference.properties && (
							<div className="mt-4 flex flex-col gap-y-4 rounded-lg border border-textInactiveLight px-4 py-4 dark:border-textInactiveDark">
								<div className="flex flex-row items-center gap-x-1">
									{displayIcon && (
										<div className="flex h-full w-4 items-start justify-center text-textPrimaryLight dark:text-textPrimaryDark">
											{mapIcon(metadata.originalReference.template.slug)}
										</div>
									)}
									<div className={'flex w-full flex-col'}>
										<div className="flex flex-row">
											<div className="flex flex-col">
												<div className="flex flex-col sm:flex-row sm:items-center sm:gap-x-1">
													<Button
														size="medium"
														variant="tertiary"
														additionalClasses="p-0 h-6 justify-start"
													>
														<TypographyLabel
															size="medium"
															isBold={true}
															additionalClasses="align-text-top"
														>
															@
														</TypographyLabel>
														<TypographyLabel size="medium" isBold={true}>
															{truncateStringInMiddle(
																metadata.originalReference.address,
															)}
														</TypographyLabel>
													</Button>
													<TypographyLabel
														size="medium"
														additionalClasses="sm:mt-0 text-textSecondaryLight dark:text-textSecondaryDark"
													>
														{mapText(metadata.originalReference)}
													</TypographyLabel>
												</div>
											</div>
											{metadata.references.attestationIPFSCID && (
												<div className="flex h-full w-5 flex-grow items-start justify-end">
													<TypographyLabel
														size="small"
														additionalClasses="pt-1.5 mr-3 text-textSecondaryLight dark:text-textSecondaryDark"
													>
														{getTimeDifferenceString(metadata.createdAt)}
													</TypographyLabel>
												</div>
											)}
										</div>
									</div>
								</div>
								{metadata.originalReference.properties.description && (
									<div>
										<TypographyParagraph
											size="medium"
											isBold={true}
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{metadata.originalReference.properties.description}
										</TypographyParagraph>
									</div>
								)}
								{metadata.properties.originalAttestationIPFSCID &&
								metadataProfileURL ? (
									<InternalLink to={metadataProfileURL} prefetch="none">
										<div className="rounded-lg border border-textInactiveLight bg-uiTertiaryLight hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:bg-uiTertiaryDark dark:hover:bg-uiSecondaryDark">
											{metadata.originalReference.properties.title && (
												<div className="border-b border-textInactiveLight px-4 py-2 dark:border-textInactiveDark">
													<TypographyLabel
														size="medium"
														additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
													>
														{metadata.originalReference.properties.title}
													</TypographyLabel>
												</div>
											)}
											{metadata.originalReference.properties.name && (
												<div className="border-b border-textInactiveLight px-4 py-2 dark:border-textInactiveDark">
													<TypographyLabel
														size="medium"
														additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
													>
														{metadata.originalReference.properties.name}
													</TypographyLabel>
												</div>
											)}
											<div className="flex flex-row items-center justify-between px-4">
												<div className="flex flex-row items-center gap-x-2 text-textPrimaryLight dark:text-textPrimaryDark">
													<EyeIcon />
													<Button variant="tertiary" additionalClasses="mt-1">
														View Attestation
													</Button>
												</div>
												<div className="text-textSecondaryLight dark:text-textSecondaryDark">
													<ArrowRightIcon />
												</div>
											</div>
										</div>
									</InternalLink>
								) : (
									<div className="rounded-lg border border-textInactiveLight bg-uiTertiaryLight hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:bg-uiTertiaryDark dark:hover:bg-uiSecondaryDark">
										{metadata.originalReference.properties.title && (
											<div className="border-b border-textInactiveLight px-4 py-2 dark:border-textInactiveDark">
												<TypographyLabel
													size="medium"
													additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
												>
													{metadata.originalReference.properties.title}
												</TypographyLabel>
											</div>
										)}
										{metadata.originalReference.properties.name && (
											<div className="border-b border-textInactiveLight px-4 py-2 dark:border-textInactiveDark">
												<TypographyLabel
													size="medium"
													additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
												>
													{metadata.originalReference.properties.name}
												</TypographyLabel>
											</div>
										)}
									</div>
								)}
							</div>
						)}
				</div>
			)}
		</div>
	)
}

export default Countersign
